import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Package, FileText, IndianRupee, Calculator, CheckCircle2, Clock, ArrowRight, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, FileCheck, Printer, Send, BookOpen, Building2, Pencil, Trash2, CalendarIcon } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { formatDate, cn } from '@/lib/utils';
import { format } from 'date-fns';
import GRNPrint from '@/components/GRNPrint';
import { formatCurrency, formatQuantity, formatRate } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseEntryPage() {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [completedEntries, setCompletedEntries] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog state
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [filteredPOs, setFilteredPOs] = useState([]);
  
  // GRN Dialog state
  const [grnDialogOpen, setGrnDialogOpen] = useState(false);
  const [selectedGrnEntry, setSelectedGrnEntry] = useState(null);
  const [printGrn, setPrintGrn] = useState(null);
  
  // Form state
  const [entryForm, setEntryForm] = useState({
    po_id: '',
    po_number: '',
    notes: '',
    items: [],
    invoice_no: '',
    invoice_received: false,
    other_charges_taxable: 0,
    gst_rate: 0,
    other_charges_gst_rate: 0,  // GST rate for other_charges when mixed
    has_mixed_gst: false,  // Toggle for per-line GST
    other_charges_non_taxable: 0,
    // Payment fields
    payment_due_date: '',
    advance_deduction: 0
  });

  // Standard Indian GST rates
  const GST_RATES = [0, 5, 12, 18, 28];

  // Invoice number editing state for GRN dialog
  const [editingInvoiceNo, setEditingInvoiceNo] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // PO advance info
  const [poAdvanceInfo, setPoAdvanceInfo] = useState(null);
  
  // Edit/Delete GRN state
  const [editGrnDialogOpen, setEditGrnDialogOpen] = useState(false);
  const [editingGrn, setEditingGrn] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingGrn, setDeletingGrn] = useState(null);

  // Totals and variance
  const [totals, setTotals] = useState({ supplier: 0, system: 0, grandTotal: 0, gstAmount: 0 });
  const [hasVariance, setHasVariance] = useState(false);
  const [totalVariance, setTotalVariance] = useState(0);

  // Helper: Check if user can edit/delete a GRN entry
  // Allowed if: owner OR has module access OR is the creator
  const canEditGrn = (entry) => {
    if (!user || !entry) return false;
    // Owner can always edit
    if (user.is_owner) return true;
    // User with purchase_entry or store_receiving module access can edit
    const hasModuleAccess = user.allowed_modules?.includes('purchase_entry') || 
                            user.allowed_modules?.includes('store_receiving');
    if (hasModuleAccess) return true;
    // Creator can edit their own entry
    if (entry.created_by === user.id) return true;
    return false;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [receiptsRes, entriesRes, posRes] = await Promise.all([
        axios.get(`${API_URL}/store/receipts?routed_to=purchase&routing_status=pending`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/store/purchase-entries`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/purchase-orders`, { headers: getAuthHeader() })
      ]);
      
      setPendingReceipts(receiptsRes.data);
      setCompletedEntries(entriesRes.data);
      setPurchaseOrders(posRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate totals and variance when items or charges change
  useEffect(() => {
    let supplierTotal = 0;
    let systemTotal = 0;
    let hasAnyVariance = false;
    let varianceTotal = 0;
    
    entryForm.items.forEach(item => {
      const supplierQty = parseFloat(item.supplier_qty) || 0;
      const supplierRate = parseFloat(item.supplier_rate) || 0;
      const supplierAmount = supplierQty * supplierRate;
      supplierTotal += supplierAmount;
      systemTotal += supplierAmount;
      
      // Check for variance
      if (item.po_price && item.po_price > 0) {
        const variance = supplierRate - item.po_price;
        if (Math.abs(variance) > 0.01) {
          hasAnyVariance = true;
          varianceTotal += variance * supplierQty;
        }
      }
    });
    
    // Calculate grand total with charges
    const otherTaxable = parseFloat(entryForm.other_charges_taxable) || 0;
    const otherNonTaxable = parseFloat(entryForm.other_charges_non_taxable) || 0;
    
    let gstAmount;
    if (entryForm.has_mixed_gst) {
      // Per-line GST: sum of line item GST + other_charges GST
      const lineItemsGst = entryForm.items.reduce((sum, item) => {
        const supplierAmount = (parseFloat(item.supplier_qty) || 0) * (parseFloat(item.supplier_rate) || 0);
        const lineGstRate = parseFloat(item.gst_rate) || 0;
        return sum + (supplierAmount * lineGstRate / 100);
      }, 0);
      const otherChargesGstRate = parseFloat(entryForm.other_charges_gst_rate) || 0;
      const otherChargesGst = otherTaxable * otherChargesGstRate / 100;
      gstAmount = lineItemsGst + otherChargesGst;
    } else {
      // Header-level GST: single rate for all
      const gstRate = parseFloat(entryForm.gst_rate) || 0;
      const taxableAmount = supplierTotal + otherTaxable;
      gstAmount = (taxableAmount * gstRate) / 100;
    }
    
    const grandTotal = supplierTotal + otherTaxable + gstAmount + otherNonTaxable;
    
    setTotals({ supplier: supplierTotal, system: systemTotal, gstAmount, grandTotal });
    setHasVariance(hasAnyVariance);
    setTotalVariance(varianceTotal);
  }, [entryForm.items, entryForm.other_charges_taxable, entryForm.gst_rate, entryForm.other_charges_non_taxable, entryForm.has_mixed_gst, entryForm.other_charges_gst_rate]);

  const openEntryDialog = (receipt) => {
    setSelectedReceipt(receipt);
    
    // Auto-populate items from store receipt with indent info
    const items = (receipt.items || []).map(item => ({
      item_id: item.item_id,
      item_name: item.item_name,
      store_qty: item.quantity_accepted || item.quantity_received,
      store_unit: item.item_unit || 'PCS',
      // Indent and PO info from store receipt
      indent_id: item.indent_id || '',
      indent_number: item.indent_number || '',
      po_id: item.po_id || '',
      po_number: item.po_number || '',
      // Bug 9 fix: carry po_line_item_index from the receipt item so the
      // PE submission can route the PO-line update to the correct index
      // (duplicate-item PO rows). store.py:4858 reads this field.
      po_line_item_index: item.po_line_item_index ?? null,
      po_price: item.po_price || null,
      // Supplier invoice fields - editable for invoice matching
      // FIX: supplier_unit should come from item.supplier_unit (PO's supplier UOM), not system_unit
      supplier_qty: '',
      supplier_unit: item.supplier_unit || item.system_unit || item.item_unit || 'PCS',
      supplier_rate: '',
      // System conversion fields - READ-ONLY from Store Receiving (quantity_accepted)
      system_qty: (item.quantity_accepted || item.quantity_received)?.toString() || '',
      system_unit: item.system_unit || item.item_unit || 'PCS',
      // Per-line GST
      gst_rate: 0
    }));
    
    setEntryForm({
      po_id: '',
      po_number: '',
      notes: '',
      items: items.length > 0 ? items : [{
        item_id: '',
        item_name: receipt.item_name || '',
        store_qty: receipt.quantity_accepted || receipt.quantity_received || 0,
        store_unit: 'PCS',
        indent_id: '',
        indent_number: '',
        po_id: '',
        po_number: '',
        po_price: null,
        // FIX: Use supplier_unit from receipt if available
        supplier_qty: '',
        supplier_unit: receipt.supplier_unit || receipt.system_unit || 'PCS',
        supplier_rate: '',
        system_qty: (receipt.quantity_accepted || receipt.quantity_received)?.toString() || '',
        system_unit: receipt.system_unit || 'PCS',
        gst_rate: 0
      }],
      invoice_no: '',
      invoice_received: false,
      other_charges_taxable: 0,
      gst_rate: 0,
      other_charges_gst_rate: 0,
      has_mixed_gst: false,
      other_charges_non_taxable: 0,
      // Payment fields
      payment_due_date: '',
      advance_deduction: 0
    });
    
    // Reset PO advance info
    setPoAdvanceInfo(null);
    
    // Filter POs that contain items from this receipt and have pending qty
    const itemIds = receipt.items?.map(item => item.item_id) || [receipt.item_id];
    const relevantPOs = purchaseOrders.filter(po => {
      // Check if PO has any of the receipt items with pending qty
      return po.line_items?.some(lineItem => {
        const pendingQty = (lineItem.quantity || 0) - (lineItem.quantity_received || 0);
        return itemIds.includes(lineItem.item_id) && pendingQty > 0;
      });
    });
    setFilteredPOs(relevantPOs);
    
    setEntryDialogOpen(true);
  };

  const handlePOSelect = async (poId) => {
    const po = purchaseOrders.find(p => p.id === poId);
    if (po) {
      setEntryForm({
        ...entryForm,
        po_id: poId,
        po_number: po.po_number
      });
      
      // Fetch advance info for this PO
      try {
        const response = await axios.get(`${API_URL}/store/po-advances/${poId}`, { headers: getAuthHeader() });
        setPoAdvanceInfo(response.data);
        // Auto-fill advance deduction with available advance
        if (response.data.available_advance > 0) {
          setEntryForm(prev => ({
            ...prev,
            po_id: poId,
            po_number: po.po_number,
            advance_deduction: response.data.available_advance
          }));
        }
      } catch (error) {
        console.error('Error fetching PO advances:', error);
        setPoAdvanceInfo(null);
      }
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...entryForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEntryForm({ ...entryForm, items: newItems });
  };

  const calculateItemAmount = (item) => {
    const supplierQty = parseFloat(item.supplier_qty) || 0;
    const supplierRate = parseFloat(item.supplier_rate) || 0;
    return supplierQty * supplierRate;
  };

  const calculateSystemRate = (item) => {
    const supplierAmount = calculateItemAmount(item);
    const systemQty = parseFloat(item.system_qty) || 0;
    return systemQty > 0 ? supplierAmount / systemQty : 0;
  };

  const getVarianceInfo = (item) => {
    if (!item.po_price || item.po_price <= 0) return null;
    const invoiceRate = parseFloat(item.supplier_rate) || 0;
    if (invoiceRate === 0) return null;
    
    const variance = invoiceRate - item.po_price;
    const variancePercent = (variance / item.po_price) * 100;
    
    if (Math.abs(variance) < 0.01) return null;
    
    return {
      amount: variance,
      percent: variancePercent,
      isHigher: variance > 0
    };
  };

  const handleSubmitEntry = async () => {
    // Validate
    const validItems = entryForm.items.filter(item => 
      item.supplier_qty && item.supplier_rate && item.supplier_unit
    );
    
    if (validItems.length === 0) {
      toast.error('Please fill supplier invoice details for at least one item');
      return;
    }
    
    // Calculate balance amount
    const advanceDeduction = parseFloat(entryForm.advance_deduction) || 0;
    const balanceAmount = totals.grandTotal - advanceDeduction;
    
    // Validate payment due date if balance > 0
    if (balanceAmount > 0 && !entryForm.payment_due_date) {
      toast.error('Please enter Payment Due Date for balance payment');
      return;
    }
    
    try {
      const payload = {
        store_receipt_id: selectedReceipt.id,
        po_id: entryForm.po_id || null,
        po_number: entryForm.po_number || null,
        notes: entryForm.notes || null,
        invoice_no: entryForm.invoice_no || null,
        invoice_received: entryForm.invoice_received,
        other_charges_taxable: parseFloat(entryForm.other_charges_taxable) || 0,
        other_charges_gst_rate: entryForm.has_mixed_gst ? (parseFloat(entryForm.other_charges_gst_rate) || 0) : (parseFloat(entryForm.gst_rate) || 0),
        gst_rate: entryForm.has_mixed_gst ? 0 : (parseFloat(entryForm.gst_rate) || 0),
        has_mixed_gst: entryForm.has_mixed_gst,
        other_charges_non_taxable: parseFloat(entryForm.other_charges_non_taxable) || 0,
        // Payment fields
        payment_due_date: entryForm.payment_due_date || null,
        advance_deduction: advanceDeduction,
        items: validItems.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name,
          indent_id: item.indent_id || null,
          indent_number: item.indent_number || null,
          // Bug 9 fix: forward po_line_item_index so backend can
          // target the correct PO line (store.py:4858).
          po_line_item_index: item.po_line_item_index ?? null,
          supplier_qty: parseFloat(item.supplier_qty),
          supplier_unit: item.supplier_unit,
          supplier_rate: parseFloat(item.supplier_rate),
          system_qty: parseFloat(item.system_qty) || parseFloat(item.supplier_qty),
          system_unit: item.system_unit || item.supplier_unit,
          gst_rate: entryForm.has_mixed_gst ? (parseFloat(item.gst_rate) || 0) : (parseFloat(entryForm.gst_rate) || 0)
        }))
      };
      
      const response = await axios.post(`${API_URL}/store/purchase-entry`, payload, { headers: getAuthHeader() });
      
      if (response.data.success) {
        let message = `Purchase entry completed! Grand Total: ${formatCurrency(totals.grandTotal)}`;
        if (hasVariance) {
          message += ` (Price variance detected!)`;
        }
        if (response.data.payment_request_id) {
          message += ` | Payment request created for ${formatCurrency(response.data.balance_amount)}`;
        }
        toast.success(message);
        setEntryDialogOpen(false);
        setPoAdvanceInfo(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating purchase entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create purchase entry');
    }
  };

  // Update invoice status for existing entry
  const handleUpdateInvoiceStatus = async (entryId, invoiceReceived, invoiceNo = null) => {
    setIsUpdatingStatus(true);
    try {
      const payload = { invoice_received: invoiceReceived };
      if (invoiceNo !== null) {
        payload.invoice_no = invoiceNo;
      }
      await axios.patch(
        `${API_URL}/store/purchase-entries/${entryId}/invoice-status`,
        payload,
        { headers: getAuthHeader() }
      );
      toast.success(invoiceReceived ? 'Invoice marked as received' : 'Invoice status cleared');
      fetchData();
      // Update the selected GRN entry if it's open
      if (selectedGrnEntry && selectedGrnEntry.id === entryId) {
        setSelectedGrnEntry(prev => ({
          ...prev,
          invoice_no: invoiceNo || prev.invoice_no,
          invoice_received: invoiceReceived,
          invoice_received_at: invoiceReceived ? new Date().toISOString() : null
        }));
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error('Failed to update invoice status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Edit GRN - update item in form
  const updateEditItem = (index, field, value) => {
    if (!editingGrn) return;
    const newItems = [...(editingGrn.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate amounts for the item
    const item = newItems[index];
    const supplierQty = parseFloat(item.supplier_qty) || 0;
    const supplierRate = parseFloat(item.supplier_rate) || 0;
    const supplierAmount = supplierQty * supplierRate;
    const systemQty = parseFloat(item.system_qty) || supplierQty;
    
    newItems[index].supplier_amount = supplierAmount;
    newItems[index].system_amount = supplierAmount;
    newItems[index].system_rate = systemQty > 0 ? supplierAmount / systemQty : 0;
    
    setEditingGrn({ ...editingGrn, items: newItems });
  };

  // Calculate edit form totals
  const calculateEditTotals = () => {
    if (!editingGrn) return { supplier: 0, gstAmount: 0, grandTotal: 0 };
    
    let supplierTotal = 0;
    (editingGrn.items || []).forEach(item => {
      const qty = parseFloat(item.supplier_qty) || 0;
      const rate = parseFloat(item.supplier_rate) || 0;
      supplierTotal += qty * rate;
    });
    
    const otherTaxable = parseFloat(editingGrn.other_charges_taxable) || 0;
    const gstRate = parseFloat(editingGrn.gst_rate) || 0;
    const otherNonTaxable = parseFloat(editingGrn.other_charges_non_taxable) || 0;
    
    const taxableAmount = supplierTotal + otherTaxable;
    const gstAmount = (taxableAmount * gstRate) / 100;
    const grandTotal = supplierTotal + otherTaxable + gstAmount + otherNonTaxable;
    
    return { supplier: supplierTotal, gstAmount, grandTotal };
  };

  const editTotals = calculateEditTotals();

  // Edit GRN handler
  const handleEditGrn = async () => {
    if (!editingGrn) return;
    
    try {
      await axios.patch(
        `${API_URL}/store/purchase-entries/${editingGrn.id}`,
        {
          invoice_dc_no: editingGrn.invoice_dc_no,
          invoice_no: editingGrn.invoice_no,
          po_id: editingGrn.po_id,
          po_number: editingGrn.po_number,
          other_charges_taxable: parseFloat(editingGrn.other_charges_taxable) || 0,
          other_charges_non_taxable: parseFloat(editingGrn.other_charges_non_taxable) || 0,
          gst_rate: parseFloat(editingGrn.gst_rate) || 0,
          notes: editingGrn.notes,
          remarks: editingGrn.remarks,
          invoice_received: editingGrn.invoice_received || false,
          items: (editingGrn.items || []).map(item => ({
            supplier_qty: parseFloat(item.supplier_qty) || 0,
            supplier_rate: parseFloat(item.supplier_rate) || 0
          }))
        },
        { headers: getAuthHeader() }
      );
      toast.success('GRN updated successfully');
      setEditGrnDialogOpen(false);
      setEditingGrn(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update GRN');
    }
  };

  // Delete GRN handler
  const handleDeleteGrn = async () => {
    if (!deletingGrn) return;
    
    try {
      await axios.delete(
        `${API_URL}/store/purchase-entries/${deletingGrn.id}`,
        { headers: getAuthHeader() }
      );
      toast.success(`GRN ${deletingGrn.grn_no} deleted successfully`);
      setDeleteConfirmOpen(false);
      setDeletingGrn(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete GRN');
    }
  };

  const handleDocsHandover = async (entryId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/store/purchase-entries/${entryId}/docs-handover`,
        { docs_handed_over: true },
        { headers: getAuthHeader() }
      );
      toast.success('Documents marked as handed over to Accounts');
      fetchData();
      if (selectedGrnEntry && selectedGrnEntry.id === entryId) {
        setSelectedGrnEntry(prev => ({
          ...prev,
          docs_handed_over: true,
          docs_handed_over_at: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error updating docs handover:', error);
      toast.error(error.response?.data?.detail || 'Failed to update docs handover status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDocsReceived = async (entryId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/store/purchase-entries/${entryId}/docs-received`,
        { docs_received: true },
        { headers: getAuthHeader() }
      );
      toast.success('Documents acknowledged as received by Accounts');
      fetchData();
      if (selectedGrnEntry && selectedGrnEntry.id === entryId) {
        setSelectedGrnEntry(prev => ({
          ...prev,
          docs_received: true,
          docs_received_at: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error updating docs received:', error);
      toast.error(error.response?.data?.detail || 'Failed to update docs received status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleTallyRecorded = async (entryId) => {
    setIsUpdatingStatus(true);
    try {
      await axios.patch(
        `${API_URL}/store/purchase-entries/${entryId}/tally-recorded`,
        { tally_recorded: true },
        { headers: getAuthHeader() }
      );
      toast.success('Invoice marked as recorded in Tally');
      fetchData();
      if (selectedGrnEntry && selectedGrnEntry.id === entryId) {
        setSelectedGrnEntry(prev => ({
          ...prev,
          tally_recorded: true,
          tally_recorded_at: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error updating tally status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update Tally status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const pendingCount = pendingReceipts.length;
  const completedCount = completedEntries.length;
  const totalCompletedValue = completedEntries.reduce((sum, e) => sum + (e.total_supplier_amount || 0), 0);
  const entriesWithVariance = completedEntries.filter(e => e.has_price_variance).length;
  const pendingInvoiceCount = completedEntries.filter(e => !e.invoice_received).length;

  // Invoice filter state
  const [invoiceFilter, setInvoiceFilter] = useState('all'); // 'all', 'pending', 'received'
  const [itemSearch, setItemSearch] = useState(''); // Item name search
  
  const filteredCompletedEntries = completedEntries.filter(entry => {
    if (invoiceFilter === 'pending') return !entry.invoice_received;
    if (invoiceFilter === 'received') return entry.invoice_received;
    // Item name search
    if (itemSearch.trim()) {
      const searchLower = itemSearch.toLowerCase();
      const hasMatchingItem = entry.items?.some(item => 
        (item.item_name || '').toLowerCase().includes(searchLower)
      );
      if (!hasMatchingItem) return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-600" />
              Purchase Entry
            </h1>
            <p className="text-muted-foreground">Enter price and PO details for received materials</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending Entry</p>
                  <p className="text-2xl font-bold">{pendingCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold">{completedCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <IndianRupee className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(totalCompletedValue)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Price Variance</p>
                  <p className="text-2xl font-bold">{entriesWithVariance}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={pendingInvoiceCount > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${pendingInvoiceCount > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  <FileCheck className={`w-5 h-5 ${pendingInvoiceCount > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Pending</p>
                  <p className={`text-2xl font-bold ${pendingInvoiceCount > 0 ? 'text-red-600' : ''}`}>{pendingInvoiceCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending Entry ({pendingCount})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Completed ({completedCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Pending Purchase Entries</CardTitle>
                <CardDescription>Store receipts waiting for price and PO details</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingReceipts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending purchase entries</p>
                    <p className="text-sm">All store receipts have been processed</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Receipt No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Invoice/DC</TableHead>
                        <TableHead className="text-center">Items</TableHead>
                        <TableHead>Indents</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReceipts.map(receipt => {
                        const indentNos = (receipt.items || [])
                          .filter(i => i.indent_number)
                          .map(i => i.indent_number);
                        return (
                          <TableRow key={receipt.id}>
                            <TableCell className="font-mono font-bold">{receipt.store_receipt_no}</TableCell>
                            <TableCell>{formatDate(receipt.receipt_date)}</TableCell>
                            <TableCell>{receipt.supplier_name}</TableCell>
                            <TableCell>{receipt.invoice_dc_no}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{receipt.items?.length || 1}</Badge>
                            </TableCell>
                            <TableCell>
                              {indentNos.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {indentNos.slice(0, 2).map((no, i) => (
                                    <Badge key={i} variant="secondary" className="text-xs">{no}</Badge>
                                  ))}
                                  {indentNos.length > 2 && (
                                    <Badge variant="secondary" className="text-xs">+{indentNos.length - 2}</Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => openEntryDialog(receipt)}>
                                Enter Details <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Completed Purchase Entries</CardTitle>
                    <CardDescription>Entries with price and PO details filled - Click View GRN to see full timestamps</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search by item name..."
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      className="w-48"
                    />
                    <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Invoice Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Entries</SelectItem>
                        <SelectItem value="pending">
                          <span className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-red-500" />
                            Invoice Pending
                          </span>
                        </SelectItem>
                        <SelectItem value="received">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Invoice Received
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCompletedEntries.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{invoiceFilter !== 'all' ? 'No entries match the filter' : 'No completed entries yet'}</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>GRN / Receipt No.</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>PO No.</TableHead>
                        <TableHead className="text-center">Items</TableHead>
                        <TableHead className="text-right">Total Amount</TableHead>
                        <TableHead className="text-center">Variance</TableHead>
                        <TableHead className="text-center">Invoice</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCompletedEntries.map(entry => (
                        <TableRow key={entry.id} className={!entry.invoice_received ? 'bg-red-50/50' : ''}>
                          <TableCell className="font-mono font-medium">{entry.store_receipt_no}</TableCell>
                          <TableCell>{entry.supplier_name}</TableCell>
                          <TableCell>{entry.po_number || '-'}</TableCell>
                          <TableCell className="text-center">{entry.items?.length || 1}</TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(entry.total_supplier_amount || 0)}
                          </TableCell>
                          <TableCell className="text-center">
                            {entry.has_price_variance ? (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {entry.total_variance_amount > 0 ? '+' : ''}
                                {formatCurrency(Math.abs(entry.total_variance_amount || 0))}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-600">OK</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {entry.invoice_received ? (
                              <Badge variant="outline" className="text-green-600 gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Received
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 gap-1">
                                <Clock className="w-3 h-3" />
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedGrnEntry(entry);
                                  setEditingInvoiceNo(entry.invoice_no || '');
                                  setGrnDialogOpen(true);
                                }}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {canEditGrn(entry) && !entry.docs_handed_over && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingGrn({...entry});
                                      setEditGrnDialogOpen(true);
                                    }}
                                    title="Edit GRN"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => {
                                      setDeletingGrn(entry);
                                      setDeleteConfirmOpen(true);
                                    }}
                                    title="Delete GRN"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Purchase Entry Dialog */}
        <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Purchase Entry
              </DialogTitle>
              <DialogDescription>
                Enter price details for receipt: <span className="font-mono font-bold">{selectedReceipt?.store_receipt_no}</span>
              </DialogDescription>
            </DialogHeader>
            
            {selectedReceipt && (
              <>
              <div className="space-y-4 py-4">
                {/* Receipt Info */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-3 gap-4">
                    <p><span className="text-muted-foreground">Supplier:</span> {selectedReceipt.supplier_name}</p>
                    <p><span className="text-muted-foreground">Invoice/DC:</span> {selectedReceipt.invoice_dc_no}</p>
                    <p><span className="text-muted-foreground">Nature:</span> {selectedReceipt.nature_of_inward}</p>
                  </div>
                </div>

                {/* Invoice No & Notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier Invoice No.</Label>
                    <Input
                      placeholder="Enter invoice number"
                      value={entryForm.invoice_no}
                      onChange={(e) => setEntryForm({ ...entryForm, invoice_no: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      placeholder="Any notes about this entry..."
                      value={entryForm.notes}
                      onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                      rows={1}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="invoice_received"
                    checked={entryForm.invoice_received}
                    onCheckedChange={(checked) => setEntryForm({ ...entryForm, invoice_received: checked })}
                  />
                  <Label htmlFor="invoice_received" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-green-600" />
                    Invoice Document Received
                  </Label>
                </div>

                {/* Items Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-2 border-b flex justify-between items-center">
                    <h4 className="font-semibold text-sm">Line Items - Enter Supplier Invoice Details</h4>
                    <div className="flex items-center gap-4">
                      {hasVariance && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Price Variance Detected
                        </Badge>
                      )}
                      <div className="flex items-center gap-2 bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
                        <Switch
                          id="mixed-gst-toggle"
                          checked={entryForm.has_mixed_gst}
                          onCheckedChange={(checked) => setEntryForm(prev => ({ ...prev, has_mixed_gst: checked }))}
                          data-testid="mixed-gst-toggle"
                        />
                        <Label htmlFor="mixed-gst-toggle" className="text-sm cursor-pointer font-medium text-amber-800">
                          Different GST rates per item
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead rowSpan={2} className="border-r w-[150px]">Item</TableHead>
                          <TableHead rowSpan={2} className="border-r w-[100px]">Indent No.</TableHead>
                          <TableHead rowSpan={2} className="border-r w-[80px]">PO Price</TableHead>
                          <TableHead colSpan={4} className="text-center border-r bg-blue-50">Supplier Invoice</TableHead>
                          <TableHead colSpan={3} className="text-center bg-green-50">System (Our Unit)</TableHead>
                          {entryForm.has_mixed_gst && (
                            <TableHead rowSpan={2} className="w-[80px] text-center bg-amber-50">GST %</TableHead>
                          )}
                          <TableHead rowSpan={2} className="w-[80px] text-center">Variance</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-center w-[70px]">Qty *</TableHead>
                          <TableHead className="text-center w-[90px]">Unit *</TableHead>
                          <TableHead className="text-center w-[90px]">Rate *</TableHead>
                          <TableHead className="text-center w-[90px] border-r">Amount</TableHead>
                          <TableHead className="text-center w-[70px]">Qty</TableHead>
                          <TableHead className="text-center w-[90px]">Unit</TableHead>
                          <TableHead className="text-center w-[80px]">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entryForm.items.map((item, index) => {
                          const supplierAmount = calculateItemAmount(item);
                          const systemRate = calculateSystemRate(item);
                          const variance = getVarianceInfo(item);
                          
                          return (
                            <TableRow key={index} className={variance ? 'bg-orange-50/50' : ''}>
                              <TableCell className="border-r">
                                <div>
                                  <span className="font-medium text-sm">{item.item_name}</span>
                                  <span className="block text-xs text-muted-foreground">
                                    Store: {item.store_qty} {item.store_unit}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="border-r">
                                {item.indent_number ? (
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {item.indent_number}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                                {item.po_number && (
                                  <Badge variant="secondary" className="font-mono text-xs mt-1 block w-fit">
                                    PO: {item.po_number}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="border-r text-center">
                                {item.po_price ? (
                                  <span className="font-medium">{formatRate(item.po_price)}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              {/* Supplier Invoice Details */}
                              <TableCell className="bg-blue-50/30">
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="w-full text-center h-8"
                                  placeholder="0"
                                  value={item.supplier_qty}
                                  onChange={(e) => updateItem(index, 'supplier_qty', e.target.value)}
                                  data-testid={`item-${index}-supplier-qty`}
                                />
                              </TableCell>
                              <TableCell className="bg-blue-50/30">
                                <div className="px-2 py-1 bg-gray-100 rounded text-center text-sm font-medium h-8 flex items-center justify-center">
                                  {item.supplier_unit || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-blue-50/30">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">₹</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    className={`pl-5 text-center h-8 ${variance ? (variance.isHigher ? 'border-red-400' : 'border-green-400') : ''}`}
                                    placeholder="0.00"
                                    value={item.supplier_rate}
                                    onChange={(e) => updateItem(index, 'supplier_rate', e.target.value)}
                                    data-testid={`item-${index}-supplier-rate`}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="bg-blue-50/30 border-r text-right font-medium text-sm">
                                {formatCurrency(supplierAmount)}
                              </TableCell>
                              {/* System Conversion - READ-ONLY (from Store Receiving) */}
                              <TableCell className="bg-green-50/30">
                                <div className="px-2 py-1 bg-gray-100 rounded text-center text-sm font-medium h-8 flex items-center justify-center" title="System qty from Store Receiving (read-only)">
                                  {item.system_qty || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-green-50/30">
                                <div className="px-2 py-1 bg-gray-100 rounded text-center text-sm font-medium h-8 flex items-center justify-center">
                                  {item.system_unit || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-green-50/30 text-right text-sm">
                                <span className="text-muted-foreground">
                                  {formatRate(systemRate)}
                                </span>
                              </TableCell>
                              {/* Per-line GST Column - only when mixed mode */}
                              {entryForm.has_mixed_gst && (
                                <TableCell className="bg-amber-50/30">
                                  <Select
                                    value={String(item.gst_rate || 0)}
                                    onValueChange={(val) => updateItem(index, 'gst_rate', parseFloat(val))}
                                  >
                                    <SelectTrigger className="h-8 w-[70px]" data-testid={`item-${index}-gst-rate`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {GST_RATES.map(rate => (
                                        <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              )}
                              {/* Variance Column */}
                              <TableCell className="text-center">
                                {variance ? (
                                  <div className={`flex items-center justify-center gap-1 ${variance.isHigher ? 'text-red-600' : 'text-green-600'}`}>
                                    {variance.isHigher ? (
                                      <TrendingUp className="w-4 h-4" />
                                    ) : (
                                      <TrendingDown className="w-4 h-4" />
                                    )}
                                    <span className="text-xs font-medium">
                                      {variance.percent > 0 ? '+' : ''}{variance.percent.toFixed(1)}%
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Optional: Link PO for Advance Deduction */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 max-w-md">
                      <Label className="text-xs text-muted-foreground">
                        Link PO for Advance Deduction (Optional)
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Select a PO to deduct advance payment. Line items already show their source PO above.
                      </p>
                      <SearchableSelect
                        options={filteredPOs.map(po => {
                          const itemIds = entryForm.items?.map(item => item.item_id) || [];
                          let pendingQty = 0;
                          po.line_items?.forEach(lineItem => {
                            if (itemIds.includes(lineItem.item_id)) {
                              pendingQty += (lineItem.quantity || 0) - (lineItem.quantity_received || 0);
                            }
                          });
                          return {
                            value: po.id,
                            label: `${po.po_number} - ${po.supplier_name || 'Unknown'} (Pending: ${pendingQty})`
                          };
                        })}
                        value={entryForm.po_id}
                        onValueChange={handlePOSelect}
                        placeholder="Select PO for advance deduction..."
                        searchPlaceholder="Search PO number..."
                        emptyText={filteredPOs.length === 0 ? "No POs with pending qty" : "No POs found"}
                      />
                    </div>
                  </div>
                </div>

                {/* Other Charges Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Additional Charges & GST
                  </h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Other Charges (Taxable)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          placeholder="0.00"
                          className="pl-7"
                          value={entryForm.other_charges_taxable}
                          onChange={(e) => setEntryForm(prev => ({ ...prev, other_charges_taxable: e.target.value }))}
                          data-testid="other-charges-taxable"
                        />
                      </div>
                    </div>
                    {/* Header GST Rate - only when NOT mixed */}
                    {!entryForm.has_mixed_gst ? (
                      <div>
                        <Label className="text-xs">GST Rate (%) - All Items</Label>
                        <Select
                          value={String(entryForm.gst_rate || 0)}
                          onValueChange={(val) => setEntryForm(prev => ({ ...prev, gst_rate: parseFloat(val) }))}
                        >
                          <SelectTrigger data-testid="header-gst-rate">
                            <SelectValue placeholder="Select GST %" />
                          </SelectTrigger>
                          <SelectContent>
                            {GST_RATES.map(rate => (
                              <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Other Charges GST (%)</Label>
                        <Select
                          value={String(entryForm.other_charges_gst_rate || 0)}
                          onValueChange={(val) => setEntryForm(prev => ({ ...prev, other_charges_gst_rate: parseFloat(val) }))}
                        >
                          <SelectTrigger data-testid="other-charges-gst-rate">
                            <SelectValue placeholder="Select GST %" />
                          </SelectTrigger>
                          <SelectContent>
                            {GST_RATES.map(rate => (
                              <SelectItem key={rate} value={String(rate)}>{rate}%</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Total GST Amount</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="text"
                          disabled
                          className="pl-7 bg-muted"
                          value={formatRate(totals.gstAmount)}
                          data-testid="total-gst-amount"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Other Charges (Non-Taxable)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          placeholder="0.00"
                          className="pl-7"
                          value={entryForm.other_charges_non_taxable}
                          onChange={(e) => setEntryForm(prev => ({ ...prev, other_charges_non_taxable: e.target.value }))}
                          data-testid="other-charges-non-taxable"
                        />
                      </div>
                    </div>
                  </div>
                  {entryForm.has_mixed_gst && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Per-item GST mode: Set GST % for each line item in the table above
                    </p>
                  )}
                </div>

                {/* Totals */}
                <div className="flex justify-end gap-4">
                  {hasVariance && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 w-64">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-orange-700 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Total Variance
                        </span>
                        <span className={`text-lg font-bold ${totalVariance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {totalVariance > 0 ? '+' : ''}{formatCurrency(totalVariance)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 w-64" data-testid="totals-summary">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">Subtotal</span>
                      <span className="text-sm">{formatCurrency(totals.supplier)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">+ Other Taxable</span>
                      <span className="text-sm">{formatCurrency(parseFloat(entryForm.other_charges_taxable) || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">
                        + GST {entryForm.has_mixed_gst ? '(Mixed)' : `(${entryForm.gst_rate || 0}%)`}
                      </span>
                      <span className="text-sm" data-testid="gst-amount-display">{formatCurrency(totals.gstAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-muted-foreground">+ Other Non-Taxable</span>
                      <span className="text-sm">{formatCurrency(parseFloat(entryForm.other_charges_non_taxable) || 0)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-blue-700">Grand Total</span>
                        <span className="text-xl font-bold text-blue-800" data-testid="grand-total-display">
                          {formatCurrency(totals.grandTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              {totals.grandTotal > 0 && (
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <IndianRupee className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Payment Details</span>
                  </div>
                  
                  {/* Advance Info */}
                  {poAdvanceInfo && poAdvanceInfo.available_advance > 0 && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Available advance from PO: {formatCurrency(poAdvanceInfo.available_advance)}</span>
                      </div>
                      {poAdvanceInfo.total_already_deducted > 0 && (
                        <div className="text-xs text-amber-600 mt-1">
                          ({formatCurrency(poAdvanceInfo.total_already_deducted)} already deducted in previous GRNs)
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-green-700">Advance Deduction (₹)</Label>
                      <Input
                        type="number"
                        value={entryForm.advance_deduction}
                        onChange={(e) => setEntryForm({ ...entryForm, advance_deduction: e.target.value })}
                        min="0"
                        max={totals.grandTotal}
                        step="0.01"
                        className="bg-white"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-green-700">
                        Balance Amount
                      </Label>
                      <div className="h-10 flex items-center px-3 bg-white border rounded-md font-bold text-green-800">
                        {formatCurrency(totals.grandTotal - (parseFloat(entryForm.advance_deduction) || 0))}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-green-700">
                        Payment Due Date {(totals.grandTotal - (parseFloat(entryForm.advance_deduction) || 0)) > 0 ? '*' : ''}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-white",
                              !entryForm.payment_due_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {entryForm.payment_due_date ? format(new Date(entryForm.payment_due_date), 'dd/MM/yyyy') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={entryForm.payment_due_date ? new Date(entryForm.payment_due_date) : undefined}
                            onSelect={(date) => setEntryForm({ ...entryForm, payment_due_date: date ? format(date, 'yyyy-MM-dd') : '' })}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  {(totals.grandTotal - (parseFloat(entryForm.advance_deduction) || 0)) > 0 && (
                    <p className="text-xs text-green-600 mt-2">
                      * A payment request will be auto-created for the balance amount
                    </p>
                  )}
                </div>
              )}
              </>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEntryDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitEntry} className={hasVariance ? 'bg-orange-600 hover:bg-orange-700' : ''}>
                {hasVariance && <AlertTriangle className="w-4 h-4 mr-2" />}
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {hasVariance ? 'Complete with Variance' : 'Complete Entry'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* GRN Details Dialog */}
        <Dialog open={grnDialogOpen} onOpenChange={setGrnDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                GRN Details
              </DialogTitle>
              <DialogDescription>
                Goods Receipt Note with complete timestamp tracking
              </DialogDescription>
            </DialogHeader>
            
            {selectedGrnEntry && (
              <div className="space-y-6 py-4">
                {/* GRN Header Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">GRN / Store Receipt No.</p>
                    <p className="font-mono font-bold text-lg">{selectedGrnEntry.store_receipt_no}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">PO Number</p>
                    <p className="font-mono font-medium">{selectedGrnEntry.po_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Supplier</p>
                    <p className="font-medium">{selectedGrnEntry.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Invoice/DC No.</p>
                    <p className="font-medium">{selectedGrnEntry.invoice_dc_no || '-'}</p>
                  </div>
                </div>

                {/* Timestamp Timeline */}
                <div className="space-y-1">
                  <h4 className="font-semibold text-sm mb-3">Receipt Timeline</h4>
                  <div className="relative pl-6 space-y-4">
                    {/* Vertical line */}
                    <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-border"></div>
                    
                    {/* Gate Entry */}
                    <div className="relative flex items-start gap-4">
                      <div className="absolute left-[-24px] w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">1</span>
                      </div>
                      <div className="flex-1 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-blue-800">Gate Entry</p>
                            <p className="text-xs text-blue-600">{selectedGrnEntry.gate_entry_no || 'N/A'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm font-medium">
                              {selectedGrnEntry.gate_entry_timestamp 
                                ? formatDate(selectedGrnEntry.gate_entry_timestamp)
                                : 'N/A'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selectedGrnEntry.gate_entry_timestamp 
                                ? new Date(selectedGrnEntry.gate_entry_timestamp).toLocaleTimeString('en-IN')
                                : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Store Receipt */}
                    <div className="relative flex items-start gap-4">
                      <div className="absolute left-[-24px] w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">2</span>
                      </div>
                      <div className="flex-1 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-green-800">Store Receipt</p>
                            <p className="text-xs text-green-600">{selectedGrnEntry.store_receipt_no}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm font-medium">
                              {selectedGrnEntry.store_receipt_timestamp 
                                ? formatDate(selectedGrnEntry.store_receipt_timestamp)
                                : 'N/A'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selectedGrnEntry.store_receipt_timestamp 
                                ? new Date(selectedGrnEntry.store_receipt_timestamp).toLocaleTimeString('en-IN')
                                : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Purchase Entry */}
                    <div className="relative flex items-start gap-4">
                      <div className="absolute left-[-24px] w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">3</span>
                      </div>
                      <div className="flex-1 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-purple-800">Purchase Entry (GRN)</p>
                            <p className="text-xs text-purple-600">Price & PO Details Entered</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm font-medium">
                              {selectedGrnEntry.purchase_entry_timestamp 
                                ? formatDate(selectedGrnEntry.purchase_entry_timestamp)
                                : 'N/A'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selectedGrnEntry.purchase_entry_timestamp 
                                ? new Date(selectedGrnEntry.purchase_entry_timestamp).toLocaleTimeString('en-IN')
                                : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Invoice & Document Workflow Section */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Invoice & Document Workflow</h4>
                  
                  {/* Invoice Number & Status */}
                  <div className={`p-4 rounded-lg border ${selectedGrnEntry.invoice_received ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-full ${selectedGrnEntry.invoice_received ? 'bg-green-100' : 'bg-amber-100'}`}>
                          <FileCheck className={`w-5 h-5 ${selectedGrnEntry.invoice_received ? 'text-green-600' : 'text-amber-600'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${selectedGrnEntry.invoice_received ? 'text-green-800' : 'text-amber-800'}`}>
                            Invoice {selectedGrnEntry.invoice_received ? 'Received' : 'Pending'}
                          </p>
                          {selectedGrnEntry.invoice_no && (
                            <p className="text-sm font-mono mt-1">Invoice No: {selectedGrnEntry.invoice_no}</p>
                          )}
                          {selectedGrnEntry.invoice_received && selectedGrnEntry.invoice_received_at && (
                            <p className="text-xs text-green-600 mt-1">
                              {formatDate(selectedGrnEntry.invoice_received_at)} {new Date(selectedGrnEntry.invoice_received_at).toLocaleTimeString('en-IN')}
                              {selectedGrnEntry.invoice_received_by && ` • ${selectedGrnEntry.invoice_received_by}`}
                            </p>
                          )}
                        </div>
                      </div>
                      {!selectedGrnEntry.invoice_received && (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Invoice No."
                            className="w-32 h-9"
                            value={editingInvoiceNo}
                            onChange={(e) => setEditingInvoiceNo(e.target.value)}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateInvoiceStatus(selectedGrnEntry.id, true, editingInvoiceNo)}
                            disabled={isUpdatingStatus}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Received
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Documents Handover to Accounts */}
                  <div className={`p-4 rounded-lg border ${
                    selectedGrnEntry.docs_handed_over 
                      ? 'bg-blue-50 border-blue-200' 
                      : selectedGrnEntry.invoice_received 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${selectedGrnEntry.docs_handed_over ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <Send className={`w-5 h-5 ${selectedGrnEntry.docs_handed_over ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${selectedGrnEntry.docs_handed_over ? 'text-blue-800' : 'text-gray-600'}`}>
                            Docs Handover to Accounts
                          </p>
                          {selectedGrnEntry.docs_handed_over && selectedGrnEntry.docs_handed_over_at && (
                            <p className="text-xs text-blue-600">
                              {formatDate(selectedGrnEntry.docs_handed_over_at)} {new Date(selectedGrnEntry.docs_handed_over_at).toLocaleTimeString('en-IN')}
                              {selectedGrnEntry.docs_handed_over_by && ` • ${selectedGrnEntry.docs_handed_over_by}`}
                            </p>
                          )}
                          {!selectedGrnEntry.docs_handed_over && !selectedGrnEntry.invoice_received && (
                            <p className="text-xs text-gray-400">Waiting for invoice to be received</p>
                          )}
                        </div>
                      </div>
                      {selectedGrnEntry.invoice_received && !selectedGrnEntry.docs_handed_over && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDocsHandover(selectedGrnEntry.id)}
                          disabled={isUpdatingStatus}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Handover Docs
                        </Button>
                      )}
                      {selectedGrnEntry.docs_handed_over && (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Accounts: Documents Received (Read-only status - Actions in GRN Documents page) */}
                  <div className={`p-4 rounded-lg border ${
                    selectedGrnEntry.docs_received 
                      ? 'bg-indigo-50 border-indigo-200' 
                      : selectedGrnEntry.docs_handed_over 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${selectedGrnEntry.docs_received ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                          <Building2 className={`w-5 h-5 ${selectedGrnEntry.docs_received ? 'text-indigo-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${selectedGrnEntry.docs_received ? 'text-indigo-800' : 'text-gray-600'}`}>
                            Accounts: Docs Received
                          </p>
                          {selectedGrnEntry.docs_received && selectedGrnEntry.docs_received_at && (
                            <p className="text-xs text-indigo-600">
                              {formatDate(selectedGrnEntry.docs_received_at)} {new Date(selectedGrnEntry.docs_received_at).toLocaleTimeString('en-IN')}
                              {selectedGrnEntry.docs_received_by && ` • ${selectedGrnEntry.docs_received_by}`}
                            </p>
                          )}
                          {!selectedGrnEntry.docs_received && selectedGrnEntry.docs_handed_over && (
                            <p className="text-xs text-gray-500">Pending acknowledgement in Accounts</p>
                          )}
                          {!selectedGrnEntry.docs_received && !selectedGrnEntry.docs_handed_over && (
                            <p className="text-xs text-gray-400">Waiting for docs handover</p>
                          )}
                        </div>
                      </div>
                      {selectedGrnEntry.docs_received && (
                        <Badge variant="outline" className="text-indigo-600 border-indigo-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Acknowledged
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Accounts: Tally Entry (Read-only status - Actions in GRN Documents page) */}
                  <div className={`p-4 rounded-lg border ${
                    selectedGrnEntry.tally_recorded 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : selectedGrnEntry.docs_received 
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${selectedGrnEntry.tally_recorded ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                          <BookOpen className={`w-5 h-5 ${selectedGrnEntry.tally_recorded ? 'text-emerald-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${selectedGrnEntry.tally_recorded ? 'text-emerald-800' : 'text-gray-600'}`}>
                            Recorded in Tally
                          </p>
                          {selectedGrnEntry.tally_recorded && selectedGrnEntry.tally_recorded_at && (
                            <p className="text-xs text-emerald-600">
                              {formatDate(selectedGrnEntry.tally_recorded_at)} {new Date(selectedGrnEntry.tally_recorded_at).toLocaleTimeString('en-IN')}
                              {selectedGrnEntry.tally_recorded_by && ` • ${selectedGrnEntry.tally_recorded_by}`}
                            </p>
                          )}
                          {!selectedGrnEntry.tally_recorded && selectedGrnEntry.docs_received && (
                            <p className="text-xs text-gray-500">Pending tally entry in Accounts</p>
                          )}
                          {!selectedGrnEntry.tally_recorded && !selectedGrnEntry.docs_received && (
                            <p className="text-xs text-gray-400">Waiting for docs acknowledgement</p>
                          )}
                        </div>
                      </div>
                      {selectedGrnEntry.tally_recorded && (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Amount Summary - Complete Financial Breakdown */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm">Financial Summary</h4>
                  <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                    {/* Items and Sub Total Row */}
                    <div className="flex justify-between items-center pb-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Items Sub Total ({selectedGrnEntry.items?.length || 0} items)</span>
                      <span className="font-medium">{formatCurrency(selectedGrnEntry.total_supplier_amount || selectedGrnEntry.sub_total || 0)}</span>
                    </div>
                    
                    {/* Other Charges Taxable */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Other Charges (Taxable)</span>
                      <span className="font-medium">{formatCurrency(selectedGrnEntry.other_charges_taxable || 0)}</span>
                    </div>
                    
                    {/* GST */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">GST ({selectedGrnEntry.gst_rate || 0}%)</span>
                      <span className="font-medium">{formatCurrency(selectedGrnEntry.gst_amount || 0)}</span>
                    </div>
                    
                    {/* Other Charges Non-Taxable */}
                    <div className="flex justify-between items-center pb-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Other Charges (Non-Taxable)</span>
                      <span className="font-medium">{formatCurrency(selectedGrnEntry.other_charges_non_taxable || 0)}</span>
                    </div>
                    
                    {/* Grand Total */}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-sm font-semibold">Grand Total</span>
                      <span className="text-xl font-bold text-green-600">{formatCurrency(selectedGrnEntry.grand_total || 0)}</span>
                    </div>
                    
                    {/* Variance if any */}
                    {selectedGrnEntry.has_price_variance && (
                      <div className="flex justify-between items-center pt-2 border-t border-border/50">
                        <span className="text-sm text-muted-foreground">Price Variance</span>
                        <span className={`font-medium ${selectedGrnEntry.total_variance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {selectedGrnEntry.total_variance_amount > 0 ? '+' : ''}{formatCurrency(Math.abs(selectedGrnEntry.total_variance_amount || 0))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                {selectedGrnEntry.items && selectedGrnEntry.items.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Items Received</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedGrnEntry.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.item_name}</TableCell>
                            <TableCell className="text-right">
                              {item.supplier_qty || item.system_qty} {item.supplier_unit || item.system_unit}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatRate(item.supplier_rate || item.system_rate || 0)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(item.supplier_amount || item.system_amount || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => {
                  if (selectedGrnEntry) {
                    setPrintGrn(selectedGrnEntry);
                  }
                }}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print GRN
              </Button>
              <Button variant="outline" onClick={() => setGrnDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* GRN Print Component */}
        {/* Note: printGrn is a Purchase Entry object from completedEntries */}
        {printGrn && (
          <GRNPrint 
            grn={printGrn} 
            purchaseEntry={printGrn}
            autoPrint={true}
            onPrintComplete={() => setPrintGrn(null)}
          />
        )}

        {/* Edit GRN Dialog - Mirrors Create GRN Dialog */}
        <Dialog open={editGrnDialogOpen} onOpenChange={setEditGrnDialogOpen}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Edit GRN - {editingGrn?.store_receipt_no}
              </DialogTitle>
              <DialogDescription>
                Update GRN details. Store quantities (system qty/unit) cannot be modified.
              </DialogDescription>
            </DialogHeader>
            
            {editingGrn && (
              <div className="space-y-4 py-4">
                {/* Receipt Info - Read Only */}
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="grid grid-cols-4 gap-4">
                    <p><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{editingGrn.supplier_name}</span></p>
                    <p><span className="text-muted-foreground">Gate Entry:</span> <span className="font-mono">{editingGrn.gate_entry_no || '-'}</span></p>
                    <p><span className="text-muted-foreground">Store Receipt:</span> <span className="font-mono">{editingGrn.store_receipt_no}</span></p>
                    <p><span className="text-muted-foreground">Created:</span> {editingGrn.created_at ? new Date(editingGrn.created_at).toLocaleDateString('en-GB') : '-'}</p>
                  </div>
                </div>

                {/* Editable Fields - Same as Create */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>PO Number</Label>
                    <Input
                      value={editingGrn.po_number || ''}
                      onChange={(e) => setEditingGrn({...editingGrn, po_number: e.target.value})}
                      placeholder="PO Number"
                      className="bg-muted/30"
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">PO cannot be changed after creation</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier Invoice No.</Label>
                    <Input
                      value={editingGrn.invoice_no || ''}
                      onChange={(e) => setEditingGrn({...editingGrn, invoice_no: e.target.value})}
                      placeholder="Enter invoice number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Invoice/DC No. (from Store)</Label>
                    <Input
                      value={editingGrn.invoice_dc_no || ''}
                      onChange={(e) => setEditingGrn({...editingGrn, invoice_dc_no: e.target.value})}
                      placeholder="Invoice or DC number"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={editingGrn.notes || ''}
                      onChange={(e) => setEditingGrn({...editingGrn, notes: e.target.value})}
                      placeholder="Any notes about this entry..."
                      rows={1}
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Checkbox
                      id="edit_invoice_received"
                      checked={editingGrn.invoice_received || false}
                      onCheckedChange={(checked) => setEditingGrn({...editingGrn, invoice_received: checked})}
                    />
                    <Label htmlFor="edit_invoice_received" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-green-600" />
                      Invoice Document Received
                    </Label>
                  </div>
                </div>

                {/* Items Table - Same layout as Create */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-2 border-b">
                    <h4 className="font-semibold text-sm">Line Items - Edit Supplier Invoice Details</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead rowSpan={2} className="border-r w-[180px]">Item</TableHead>
                          <TableHead rowSpan={2} className="border-r w-[100px]">Indent No.</TableHead>
                          <TableHead rowSpan={2} className="border-r w-[80px]">PO Price</TableHead>
                          <TableHead colSpan={4} className="text-center border-r bg-blue-50">Supplier Invoice (Editable)</TableHead>
                          <TableHead colSpan={3} className="text-center bg-gray-100">System (Read-Only)</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-center w-[80px] bg-blue-50">Qty</TableHead>
                          <TableHead className="text-center w-[80px] bg-blue-50">Unit</TableHead>
                          <TableHead className="text-center w-[90px] bg-blue-50">Rate (₹)</TableHead>
                          <TableHead className="text-center w-[100px] border-r bg-blue-50">Amount</TableHead>
                          <TableHead className="text-center w-[80px] bg-gray-100">Qty</TableHead>
                          <TableHead className="text-center w-[80px] bg-gray-100">Unit</TableHead>
                          <TableHead className="text-center w-[90px] bg-gray-100">Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(editingGrn.items || []).map((item, index) => {
                          const supplierQty = parseFloat(item.supplier_qty) || 0;
                          const supplierRate = parseFloat(item.supplier_rate) || 0;
                          const supplierAmount = supplierQty * supplierRate;
                          const systemQty = parseFloat(item.system_qty) || supplierQty;
                          const systemRate = systemQty > 0 ? supplierAmount / systemQty : 0;
                          
                          return (
                            <TableRow key={index}>
                              <TableCell className="border-r">
                                <div>
                                  <span className="font-medium text-sm">{item.item_name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="border-r">
                                {item.indent_number ? (
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {item.indent_number}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="border-r text-center">
                                {item.po_price ? (
                                  <span className="font-medium">{formatRate(Number(item.po_price))}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              {/* Supplier Invoice Details - EDITABLE */}
                              <TableCell className="bg-blue-50/30">
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="w-full text-center h-8"
                                  value={item.supplier_qty || ''}
                                  onChange={(e) => updateEditItem(index, 'supplier_qty', e.target.value)}
                                />
                              </TableCell>
                              <TableCell className="bg-blue-50/30">
                                <div className="px-2 py-1 bg-gray-100 rounded text-center text-sm font-medium h-8 flex items-center justify-center">
                                  {item.supplier_unit || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-blue-50/30">
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="w-full text-center h-8"
                                  value={item.supplier_rate || ''}
                                  onChange={(e) => updateEditItem(index, 'supplier_rate', e.target.value)}
                                />
                              </TableCell>
                              <TableCell className="bg-blue-50/30 border-r text-right font-medium text-sm">
                                {formatCurrency(supplierAmount)}
                              </TableCell>
                              {/* System - READ ONLY (greyed out) */}
                              <TableCell className="bg-gray-100">
                                <div className="px-2 py-1 bg-gray-200 rounded text-center text-sm font-medium h-8 flex items-center justify-center text-muted-foreground">
                                  {item.system_qty || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-gray-100">
                                <div className="px-2 py-1 bg-gray-200 rounded text-center text-sm font-medium h-8 flex items-center justify-center text-muted-foreground">
                                  {item.system_unit || '-'}
                                </div>
                              </TableCell>
                              <TableCell className="bg-gray-100 text-right text-sm text-muted-foreground">
                                {formatRate(systemRate)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Other Charges Section - Same as Create */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Additional Charges
                  </h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Other Charges (Taxable)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          placeholder="0.00"
                          className="pl-7"
                          value={editingGrn.other_charges_taxable || ''}
                          onChange={(e) => setEditingGrn({...editingGrn, other_charges_taxable: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">GST Rate (%)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          placeholder="0"
                          value={editingGrn.gst_rate || ''}
                          onChange={(e) => setEditingGrn({...editingGrn, gst_rate: e.target.value})}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">GST Amount (Calculated)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="text"
                          disabled
                          className="pl-7 bg-muted"
                          value={formatRate(editTotals.gstAmount)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Other Charges (Non-Taxable)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          placeholder="0.00"
                          className="pl-7"
                          value={editingGrn.other_charges_non_taxable || ''}
                          onChange={(e) => setEditingGrn({...editingGrn, other_charges_non_taxable: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Totals - Same as Create */}
                <div className="flex justify-end">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 w-72">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">Subtotal (Items)</span>
                      <span className="text-sm">{formatCurrency(editTotals.supplier)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">+ Other Taxable</span>
                      <span className="text-sm">{formatCurrency(parseFloat(editingGrn.other_charges_taxable) || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">+ GST ({editingGrn.gst_rate || 0}%)</span>
                      <span className="text-sm">{formatCurrency(editTotals.gstAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-muted-foreground">+ Other Non-Taxable</span>
                      <span className="text-sm">{formatCurrency(parseFloat(editingGrn.other_charges_non_taxable) || 0)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-blue-700">Grand Total</span>
                        <span className="text-xl font-bold text-blue-800">
                          {formatCurrency(editTotals.grandTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    value={editingGrn.remarks || ''}
                    onChange={(e) => setEditingGrn({...editingGrn, remarks: e.target.value})}
                    placeholder="Any remarks..."
                    rows={2}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditGrnDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleEditGrn}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete GRN Confirmation */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete GRN?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deletingGrn?.grn_no}</strong>?
                <br /><br />
                This will:
                <ul className="list-disc ml-4 mt-2">
                  <li>Reverse inventory updates (reduce stock)</li>
                  <li>Update PO received quantities</li>
                  <li>Permanently remove this GRN record</li>
                </ul>
                <br />
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteGrn}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete GRN
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
